import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useGetUniqueStaffsUnderOwner } from "repositories/office-repository";
import {
  addEditPolicyAcknowledgementTemplate,
  enablePolicyAcknowledgement,
  usePolicyAcknowledgementTemplate,
} from "repositories/office-policy-management-repository";
import { sortBy } from "lodash";
import toast from "react-hot-toast";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { handleError, handleSuccess } from "utils";
import constants from "../../../../constants";

const usePolicyAcknowledgeTemplateHook = (history) => {
  const {
    selectedId: ownerId,
    isOwner: isAccountOwner,
    isStaff,
    dataAccessValues,
    getCommonAccessLoading,
    ownerList,
    ownerListLoading,
    switchNewAccountOwner: switchNewAccountOwnerCommon,
    commonOwnerData,
  } = useAccountOwnerCommon();

  const [errors, setErrors] = useState({
    Title: "",
    Summary: "",
    ApprovedBy: "",
    approvers: "",
  });
  const [approverErrors, setApproverErrors] = useState({});
  const [showSwitchOwnerModal, setShowSwitchOwnerModal] = useState(false);
  const submittedOnceRef = useRef(false);
  const formRef = useRef(null);
  const [states, setStates] = useState({
    Summary: "",
    Title: "",
    ApprovedById: 0,
    OwnerId: ownerId ?? 0,
    owner: null,
    Acknowledgement: "",
    id: 0,
  });
  const [acknowledgementToggleOn, setAcknowledgementToggleOn] = useState(true);
  const [approverToggleOn, setApproverToggleOn] = useState(true); // Default enabled
  const [approvers, setApprovers] = useState([]);
  const [savedApprovers, setSavedApprovers] = useState([]); // Store approvers when toggle is off
  const [showStaffSelectionModal, setShowStaffSelectionModal] = useState(false);
  const [currentApproverId, setCurrentApproverId] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [approverToDelete, setApproverToDelete] = useState(null);
  // Initialize with default approver if none exist
  useEffect(() => {
    if (approvers.length === 0) {
      setApprovers([
        {
          id: -1,
          isDefault: true,
          selectionMethod: "staff",
          staffId: null,
          name: "",
          title: "",
          isDeleted: false,
        },
      ]);
    }
  }, []);

  // Handle approver toggle change
  const handleApproverToggleChange = (newValue) => {
    if (approverToggleOn && !newValue) {
      // Toggle is being turned off - save current approvers before clearing
      setSavedApprovers([...approvers]);
      // Clear approvers from state when toggle is off
      setApprovers([]);
    } else if (!approverToggleOn && newValue) {
      // Toggle is being turned on - restore approvers from savedApprovers
      if (savedApprovers.length > 0) {
        // Restore from previously saved approvers (loaded from API or previously saved)
        setApprovers([...savedApprovers]);
      } else {
        // No saved approvers - create default empty approver
        setApprovers([
          {
            id: -1,
            isDefault: true,
            selectionMethod: "staff",
            staffId: null,
            name: "",
            title: "",
            isDeleted: false,
          },
        ]);
      }
    }
    setApproverToggleOn(newValue);
  };

  // Add new approver (max 10)
  const addApprover = () => {
    if (approvers.length >= 10) return;

    // Generate unique negative ID for new approvers
    const existingIds = approvers.map((approver) => approver.id);
    let newId = -1;
    while (existingIds.includes(newId)) {
      newId--;
    }

    setApprovers([
      ...approvers,
      {
        id: newId,
        isDefault: false,
        selectionMethod: "staff",
        staffId: null,
        name: "",
        title: "",
        isDeleted: false,
      },
    ]);
  };

  // Update approver
  const updateApprover = (id, updates) => {
    setApprovers(
      approvers.map((approver) =>
        approver.id === id ? { ...approver, ...updates } : approver
      )
    );
  };

  // Delete approver - show confirmation modal or delete directly for empty approvers
  const deleteApprover = (id) => {
    const approverToDeleteData = approvers.find(
      (approver) => approver.id === id
    );

    // Check if approver is empty (no meaningful data filled)
    const isApproverEmpty =
      !approverToDeleteData?.name?.trim() &&
      !approverToDeleteData?.title?.trim() &&
      !approverToDeleteData?.staffId;

    // If approver is empty, delete directly without confirmation
    if (isApproverEmpty) {
      setApprovers(approvers.filter((approver) => approver.id !== id));
      return;
    }

    // Otherwise, show confirmation modal
    setApproverToDelete(id);
    setConfirmDeleteModal(true);
  };

  // Confirm delete approver
  const confirmDeleteApprover = async () => {
    if (!approverToDelete) return;

    try {
      const approverToDeleteData = approvers.find(
        (approver) => approver.id === approverToDelete
      );

      // If it's an existing approver (has positive ID), mark as deleted via API
      if (approverToDeleteData && approverToDeleteData.id > 0) {
        const transformedApprovers = approvers.map((approver) => ({
          Id: approver.id < 0 ? 0 : approver.id, // Convert negative IDs to 0 for new approvers
          ApproverId:
            approver.selectionMethod === "manual"
              ? null
              : approver.staffId || null,
          ApproverName: approver.name || "", // This will be used for manual approvers
          Title: approver.title || "",
          IsDeleted: approver.id === approverToDelete, // Mark the specific one as deleted
          IsDefaultApprover: approver.isDefault || false,
        }));

        const params = {
          Acknowledgement: states.Acknowledgement,
          Summary: states.Summary,
          Title: states.Title,
          ApprovedById: states.ApprovedById || null,
          OwnerId: ownerId,
          Approvers: transformedApprovers,
          isApprovedByEnabled: approverToggleOn,
        };

        if (states.id) {
          params.Id = states.id;
        }

        await addEditPolicyAcknowledgementTemplate(params, !!states.id);
        toast.success("Approver deleted successfully");
      }

      // Remove from UI
      setApprovers(
        approvers.filter((approver) => approver.id !== approverToDelete)
      );

      // Close modal
      setConfirmDeleteModal(false);
      setApproverToDelete(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Cancel delete
  const cancelDeleteApprover = () => {
    setConfirmDeleteModal(false);
    setApproverToDelete(null);
  };

  // Mark approver as default
  const markAsDefault = async (id) => {
    try {
      // First update UI to show only one default
      setApprovers(
        approvers.map((approver) => ({
          ...approver,
          isDefault: approver.id === id,
        }))
      );

      // Call API to save the default approver
      const transformedApprovers = approvers.map((approver) => ({
        Id: approver.id < 0 ? 0 : approver.id, // Convert negative IDs to 0 for new approvers
        ApproverId:
          approver.selectionMethod === "manual"
            ? null
            : approver.staffId || null,
        ApproverName: approver.name || "", // This will be used for manual approvers
        Title: approver.title || "",
        IsDeleted: approver.isDeleted || false,
        IsDefaultApprover: approver.id === id, // Only the selected one is true
      }));

      const params = {
        Acknowledgement: states.Acknowledgement,
        Summary: states.Summary,
        Title: states.Title,
        ApprovedById: states.ApprovedById || null,
        OwnerId: ownerId,
        Approvers: transformedApprovers,
        isApprovedByEnabled: approverToggleOn,
      };

      if (states.id) {
        params.Id = states.id;
      }

      await addEditPolicyAcknowledgementTemplate(params, !!states.id);
      toast.success("Default approver updated successfully");
    } catch (err) {
      toast.error(err.message);
      // Revert UI changes on error
      setApprovers(
        approvers.map((approver) => ({
          ...approver,
          isDefault: approver.isDefault, // Keep original state
        }))
      );
    }
  };

  // Open staff selection modal
  const openStaffSelectionModal = (approverId) => {
    setCurrentApproverId(approverId);
    setShowStaffSelectionModal(true);
  };

  // Handle staff selection from modal
  const handleStaffSelection = (staffMember) => {
    if (currentApproverId) {
      updateApprover(currentApproverId, {
        staffId: staffMember.id,
        name: `${staffMember.firstName} ${staffMember.lastName}`,
        title: "", // Let user add title manually
      });
    }
    setShowStaffSelectionModal(false);
    setCurrentApproverId(null);
  };

  // useEffect(() => {
  //   setToggleOn(!toggleOn);
  // }, [toggleOn]);

  const handleEnablePolicyAcknowledgement = async () => {
    const newToggleState = !acknowledgementToggleOn;
    setAcknowledgementToggleOn(newToggleState);

    // setShowLoader(true);
    try {
      const params = {
        Id: states.id,
        OwnerId: ownerId,
        IsActive: newToggleState,
      };

      const res = await enablePolicyAcknowledgement(params);
      handleSuccess(res.message);
    } catch (error) {
      handleError(error);
      // setAcknowledgementToggleOn(acknowledgementToggleOn);
    } finally {
      // setShowLoader(false);
    }
  };

  const isInValid = useCallback(() => {
    const prevStates = {
      Title: "",
      Summary: "",
      ApprovedBy: "",
      approvers: "",
    };

    // Summary validation
    if (!states.Summary.trim()) {
      prevStates.Summary = "Please enter Summary";
    } else {
      prevStates.Summary = "";
    }

    // Approver validation when toggle is on
    let hasValidApprover = false;
    let hasApproverErrors = false;

    if (approverToggleOn) {
      // First check if there are any approvers at all
      if (approvers.length === 0) {
        prevStates.approvers = "Please add at least one approver";
        setApproverErrors({});
      } else {
        // Check approvers synchronously
        const newApproverErrors = {};

        approvers.forEach((approver) => {
          const approverId = approver.id;
          const errors = {};

          if (approver.selectionMethod === "staff") {
            if (!approver.staffId) {
              errors.staff = "Please select a staff member";
              hasApproverErrors = true;
            }
            if (!approver.title?.trim()) {
              errors.title = "Please enter title";
              hasApproverErrors = true;
            }
            if (approver.staffId && approver.title?.trim()) {
              hasValidApprover = true;
            }
          } else if (approver.selectionMethod === "manual") {
            if (!approver.name?.trim()) {
              errors.name = "Please enter name";
              hasApproverErrors = true;
            }
            if (!approver.title?.trim()) {
              errors.title = "Please enter title";
              hasApproverErrors = true;
            }
            if (approver.name?.trim() && approver.title?.trim()) {
              hasValidApprover = true;
            }
          }

          if (Object.keys(errors).length > 0) {
            newApproverErrors[approverId] = errors;
          }
        });

        // Update approver errors state
        setApproverErrors(newApproverErrors);

        // Check if there's at least one valid approver
        if (!hasValidApprover) {
          prevStates.approvers =
            "Please add at least one approver with both name and title";
        } else {
          prevStates.approvers = "";
        }
      }
    } else {
      prevStates.approvers = "";
      setApproverErrors({});
    }

    setErrors(prevStates);

    // Check both main errors and approver errors
    const hasMainErrors = Object.values(prevStates).some((res) => res !== "");

    return hasMainErrors || hasApproverErrors;
  }, [states, approverToggleOn, approvers]);
  const scrollToError = useCallback(() => {
    setTimeout(() => {
      const error = formRef.current.getElementsByClassName("error-msg");
      if (error?.length) {
        error[0].scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }, 1000);
  }, []);
  useEffect(() => {
    if (submittedOnceRef.current && isInValid()) {
      scrollToError();
    }
  }, [states]);
  const [submittingLoading, setSubmittingLoading] = useState(false);

  const staffCanAccess = isStaff && dataAccessValues?.isAdmin;
  const canAccessThisPage = isAccountOwner || staffCanAccess;

  const switchNewAccountOwner = (owner, dontSet) => {
    if (owner) {
      setStates((prev) => ({
        ...prev,
        owner,
        OwnerId: owner?.id,
      }));
      if (dontSet) return;
      switchNewAccountOwnerCommon(owner);
    }
  };
  useEffect(() => {
    if (isStaff && ownerList?.length) {
      switchNewAccountOwner(commonOwnerData, true);
    }
  }, [ownerId]);
  const {
    data: policyData,
    isLoading: policyDataLoading,
    isFetching: policyDataFetching,
    refetch,
  } = usePolicyAcknowledgementTemplate(ownerId, {
    enabled: !!ownerId && canAccessThisPage,
  });
  const onSubmitHandler = async (e) => {
    e.preventDefault();
    submittedOnceRef.current = true;
    if (isInValid()) {
      scrollToError();
      return;
    }
    setSubmittingLoading(true);
    try {
      const isEdit = !!states.id;

      // Transform approvers to match API format
      const transformedApprovers = approverToggleOn
        ? approvers.map((approver) => ({
            Id: approver.id < 0 ? 0 : approver.id, // Convert negative IDs to 0 for new approvers, keep positive IDs for existing
            ApproverId:
              approver.selectionMethod === "manual"
                ? null
                : approver.staffId || null,
            ApproverName: approver.name || "", // This will be used for manual approvers
            Title: approver.title || "",
            IsDeleted: approver.isDeleted || false,
            IsDefaultApprover: approver.isDefault || false,
          }))
        : [];

      const params = {
        Acknowledgement: states.Acknowledgement,
        Summary: states.Summary,
        Title: states.Title,
        ApprovedById: states.ApprovedById || null,
        OwnerId: ownerId,
        Approvers: transformedApprovers,
        isApprovedByEnabled: approverToggleOn,
      };
      if (isEdit) {
        params.Id = states.id;
      }
      const response = await addEditPolicyAcknowledgementTemplate(
        params,
        isEdit
      );
      if (!isEdit) {
        refetch();
      }
      toast.success(response.message);
      history.push(constants.routes.accountPreferences);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmittingLoading(false);
    }
  };

  useEffect(() => {
    if (policyData?.data) {
      const prevState = policyData?.data;
      setStates((prev) => ({
        ...prev,
        Title: prevState?.title || "",
        Summary: prevState?.summary || "",
        ApprovedById: prevState?.approvedById || 0,
        Acknowledgement: prevState?.acknowledgement || "",
        id: prevState.id,
      }));

      // Set acknowledgement toggle based on isActive from API response
      setAcknowledgementToggleOn(prevState?.isActive);

      // Set approver toggle based on isApprovedByEnabled from API response
      const isApproverEnabled = prevState?.isApprovedByEnabled ?? false;
      setApproverToggleOn(isApproverEnabled);

      // Load approver data if it exists
      if (
        prevState?.policyAcknowledgementApprovers &&
        Array.isArray(prevState.policyAcknowledgementApprovers)
      ) {
        const mappedApprovers = prevState.policyAcknowledgementApprovers
          .filter((approverData) => !approverData.isDeleted) // Filter out deleted approvers
          .map((approverData) => ({
            id: approverData.id, // Keep existing ID for existing approvers
            isDefault: approverData.isDefaultApprover || false,
            selectionMethod: approverData.approverId ? "staff" : "manual", // Determine based on whether approverId exists
            staffId: approverData.approverId || null,
            name: approverData.approver
              ? `${approverData.approver.firstName} ${approverData.approver.lastName}`
              : approverData.approverName || "", // Handle manual approvers with approverName field
            title: approverData.title || "",
            isDeleted: approverData.isDeleted || false,
          }));

        // Always save mapped approvers to savedApprovers so they can be restored when toggle is turned on
        setSavedApprovers(mappedApprovers);

        // Only set approvers in state if toggle is enabled
        if (isApproverEnabled) {
          setApprovers(mappedApprovers);
        } else {
          // Toggle is off, clear approvers from state but keep them in savedApprovers
          setApprovers([]);
        }
      } else if (prevState?.approvers && Array.isArray(prevState.approvers)) {
        // Fallback for old format
        setSavedApprovers(prevState.approvers);
        if (isApproverEnabled) {
          setApprovers(prevState.approvers);
        } else {
          setApprovers([]);
        }
      } else {
        // No approvers in API response
        setSavedApprovers([]);
        setApprovers([]);
      }
    }
  }, [policyData]);

  const { data: staffList, isLoading: staffListLoading } =
    useGetUniqueStaffsUnderOwner(ownerId, {
      enabled: !!ownerId && canAccessThisPage,
    });
  const sortedOwnerList = useMemo(() => {
    return sortBy(ownerList || [], [(owner) => owner.firstName?.toLowerCase()]);
  }, [ownerList]);

  return {
    ownerList: sortedOwnerList,
    ownerListLoading,
    staffList: staffList?.data,
    staffListLoading,
    canAccessThisPage,
    getCommonAccessLoading,
    showSwitchOwnerModal,
    setShowSwitchOwnerModal,
    setStates,
    isAccountOwner,
    isStaff,
    states,
    submittedOnceRef,
    policyData,
    policyDataLoading:policyDataLoading||policyDataFetching,
    onSubmitHandler,
    submittingLoading,
    switchNewAccountOwner,
    errors,
    approverErrors,
    formRef,
    acknowledgementToggleOn,
    setAcknowledgementToggleOn,
    approverToggleOn,
    setApproverToggleOn: handleApproverToggleChange,
    approvers,
    addApprover,
    updateApprover,
    deleteApprover,
    markAsDefault,
    showStaffSelectionModal,
    setShowStaffSelectionModal,
    openStaffSelectionModal,
    handleStaffSelection,
    confirmDeleteModal,
    setConfirmDeleteModal,
    confirmDeleteApprover,
    cancelDeleteApprover,
    approverToDelete,
    handleEnablePolicyAcknowledgement,
    currentApproverId,
  };
};
export default usePolicyAcknowledgeTemplateHook;
